import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

import { UserResource, axiosInstance } from "@follosoft/react-components";
import i18n from "@/i18n";
import { ExtendedUserResource } from "@/types/extended-user";

interface UserContextType {
  user: UserResource | null;
  login: (userData: UserResource, token: string) => void;
  setUser: (userData: UserResource) => void;
  logout: () => void;
  loading: boolean;
  loaded: boolean;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserContextProvider");
  }

  return context;
};
export function useExtendedUser() {
  const result = useUser();

  return {
    ...result,
    user: result.user as ExtendedUserResource | null,
  };
}
interface UserContextProviderProps {
  children: ReactNode;
}

/**
 * Quote and payment emails link here with a signed `authenticate_url` that the
 * API exchanges for a customer token, so the recipient never has to type a
 * contract and reference number. Read once, before the router mounts, and
 * dropped from the address bar as soon as it has been used.
 */
const readAuthenticateUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);

  return params.get("authenticate_url");
};

const clearAuthenticateUrlFromAddressBar = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete("authenticate_url");
  url.searchParams.delete("lang");

  window.history.replaceState({}, "", url.pathname + url.search + url.hash);
};

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const [user, setUser] = useState<UserResource | null>(null);
  const [token, setToken] = useState<string | null>(
    sessionStorage.getItem("jwt_token"),
  );
  const [authenticateUrl] = useState<string | null>(readAuthenticateUrl);
  const [autoLoginPending, setAutoLoginPending] = useState<boolean>(
    () => readAuthenticateUrl() !== null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!authenticateUrl) {
      return;
    }

    const authenticateFromLink = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance().post(authenticateUrl, {});
        const data = response.data?.data;
        const jwtToken = data?.meta?.jwt_token;
        const contractId = data?.meta?.contract_id;
        const lang = data?.meta?.lang;

        if (contractId) {
          sessionStorage.setItem("contract_id", String(contractId));
        }
        if (lang) {
          i18n.changeLanguage(lang);
        }
        if (jwtToken) {
          sessionStorage.setItem("jwt_token", jwtToken);
          setToken(jwtToken);
        }
        setUser(data);
      } catch (error) {
        // An expired or tampered link falls through to the login form.
        setUser(null);
      } finally {
        clearAuthenticateUrlFromAddressBar();
        setAutoLoginPending(false);
        setLoading(false);
        setLoaded(true);
      }
    };

    authenticateFromLink();
  }, [authenticateUrl]);

  useEffect(() => {
    if (autoLoginPending) {
      return;
    }
    if (!token || user) {
      setLoaded(true);

      return;
    }
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance().get("/folloportal/me");

        setUser(response.data.data);
      } catch (error) {
        //TODO add error handling
        setUser(null);
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };

    fetchUser();
  }, [autoLoginPending, token, user]);

  const login = (userData: UserResource, token: string) => {
    setUser(userData);
    sessionStorage.setItem("jwt_token", token);
    setToken(token);
  };

  const logout = () => {
    setUser(null);
  };

  const value: UserContextType = {
    user,
    setUser,
    login,
    logout,
    loading,
    loaded,
    isAuthenticated: !!user,
  };

  return (
    <UserContext.Provider value={value}>
      {!loaded ? "" : children}
    </UserContext.Provider>
  );
};
